import React, { useState, useEffect } from 'react';
import { PieChart as Chart, Pie, Tooltip, Cell } from 'recharts';
import { formatLargeNumber } from '../../../../utility/formatNumbers';
import classNames from '../../../../utility/classNames';
import FormUIOptions from '../../formElements/formUIOptions';
import { checkCondition } from '../../utility/filterSchema';

export default function PieChart({ chartSchema, data }) {
  const { title, schemas } = chartSchema;

  const filteredSchemas = schemas.filter((schema) => {
    if (schema.condition) {
      return checkCondition(schema.condition, data);
    }
    return true;
  });

  const [currSchema, setCurrSchema] = useState(filteredSchemas[0]);
  const [chartData, setChartData] = useState([]);

  const COLORS = ['#0C4340', '#2CB96D', '#C2D4C4', '#218C52', '#A0E8C1'];

  useEffect(() => {
    const chartData =
      currSchema.schema[0].type !== FormUIOptions.Table
        ? currSchema.schema.map((entry) => {
            if (entry.condition) {
              if (!checkCondition(entry.condition, data)) {
                return null;
              }
            }
            const { name, label, unit, multiplier } = entry;
            const value = data[name];
            if (multiplier) {
              return { name: label, value: value * multiplier, unit };
            }
            return { name: label, value, unit };
          })
        : Array.isArray(data[currSchema.schema[0].name]) &&
          data[currSchema.schema[0].name].map((entry, i) => {
            const tableSchema = currSchema.schema[0].schema;
            const label =
              entry[tableSchema[0].name] || `${tableSchema[0].label} ${i + 1}`;
            const value = entry[tableSchema[1].name];
            const { unit, multiplier } = tableSchema[1];

            if (multiplier) {
              return { name: label, value: value * multiplier, unit };
            }
            return { name: label, value, unit };
          });

    setChartData(chartData || []);
  }, [currSchema, data]);

  const renderLabel = function (entry) {
    const { name, cx, cy, midAngle, innerRadius, outerRadius } = entry;

    if (Math.abs(entry.startAngle - entry.endAngle) < 25) {
      return null;
    }

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.3;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={'#262626'}
        className="text-sm text-neutral-500"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        <tspan className="whitespace-pre-wrap">{name}</tspan>
      </text>
    );
  };

  const renderToolTip = function (entry) {
    const { payload } = entry;
    if (payload && payload.length) {
      const { name, value, unit } = payload[0].payload;
      return (
        <div className="bg-white p-3 shadow-md rounded-md focus:outline-none">
          <div className="text-sm text-neutral-500">{name}</div>
          <div className="text-sm text-neutral-700">
            {unit === '$'
              ? `${unit}${formatLargeNumber(value)}`
              : `${value}${unit}`}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {filteredSchemas.length === 1 ? (
        <h2 className="text-center text-lg font-medium text-black">{title}</h2>
      ) : (
        <>
          <div className="flex items-center justify-center">
            <div className="border border-neutral-500/50 p-[3px] rounded-[3px] grid grid-cols-2">
              {filteredSchemas?.map((sch, index) => {
                return (
                  <button
                    key={index}
                    className={classNames(
                      'px-3 text-base relative py-1',
                      currSchema.title === sch.title
                        ? 'bg-primary-500 rounded-[3px] text-white '
                        : 'text-neutral-500'
                    )}
                    onClick={() => setCurrSchema(sch)}
                  >
                    <span className="button-content">{sch.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
      <Chart width={350} height={350}>
        <Pie
          dataKey="value"
          data={chartData}
          isAnimationActive={false}
          cx="50%"
          cy="50%"
          innerRadius={30}
          outerRadius={80}
          fill="#1378F7"
          label={renderLabel}
          labelLine={false}
        >
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={renderToolTip} />
      </Chart>
    </div>
  );
}
